/**
 * Click-to-place state: placement mode, the spec the next antenna is built
 * from, the clicked point awaiting confirmation (the dialog is open while it is
 * set) and the antennas placed this session. Session only, not persisted: the
 * placed antennas live in the loader dataset, which is not persisted either.
 */
import { create } from 'zustand';
import { DEFAULT_PLACEMENT_SPEC, type PlacementSpec } from './placement';

export interface PlacementTarget {
  lat: number;
  lon: number;
}

function defaultSpec(): PlacementSpec {
  return { ...DEFAULT_PLACEMENT_SPEC, bands: [...DEFAULT_PLACEMENT_SPEC.bands] };
}

interface PlacementState {
  /** Placement mode on: crosshair, ghost under the pointer, the next ground click opens the dialog. */
  active: boolean;
  /** The spec the next antenna is built from. Edits stick across cancels; name and short name clear after a placement. */
  pending: PlacementSpec;
  /** The clicked ground point awaiting confirmation; the placement dialog is open while it is set. */
  target: PlacementTarget | null;
  /** Antennas placed this session that have not been removed, oldest first. */
  placedIds: string[];
  /** Every id issued this session, removed ones included; id sequencing counts them so an id is never reused. */
  issuedIds: string[];
  lastPlacedId: string | null;

  /** Placement mode on (an open dialog is dropped). */
  start: () => void;
  /** Start, or cancel when already on. */
  toggle: () => void;
  setPending: (patch: Partial<PlacementSpec>) => void;
  /** A ground click while placing; ignored when placement is off or a point is already awaiting confirmation. */
  setTarget: (lat: number, lon: number) => void;
  /** Placement mode off and the dialog closed; pending edits are kept. */
  cancel: () => void;
  /** After the antenna joined the dataset: record it, mode off, dialog closed, name and short name cleared. */
  placed: (id: string) => void;
  /** After a placed antenna left the dataset. */
  removed: (id: string) => void;
  /**
   * Keep placedIds to the antennas the dataset still holds (a network switch or
   * a D1 sync replaces the antenna list); no dataset also ends placement mode.
   */
  reconcile: (datasetAntennaIds: readonly string[] | null) => void;
  /** Back to the initial state. */
  reset: () => void;
}

export const usePlacement = create<PlacementState>()((set, get) => ({
  active: false,
  pending: defaultSpec(),
  target: null,
  placedIds: [],
  issuedIds: [],
  lastPlacedId: null,

  start: () => set({ active: true, target: null }),
  toggle: () => (get().active ? get().cancel() : get().start()),
  setPending: (patch) =>
    set((s) => ({ pending: { ...s.pending, ...patch, bands: [...(patch.bands ?? s.pending.bands)] } })),
  setTarget: (lat, lon) => {
    const s = get();
    if (!s.active || s.target) return;
    set({ target: { lat, lon } });
  },
  cancel: () => set({ active: false, target: null }),
  placed: (id) =>
    set((s) => ({
      active: false,
      target: null,
      placedIds: s.placedIds.includes(id) ? s.placedIds : [...s.placedIds, id],
      issuedIds: s.issuedIds.includes(id) ? s.issuedIds : [...s.issuedIds, id],
      lastPlacedId: id,
      pending: { ...s.pending, name: '', shortName: '' },
    })),
  removed: (id) =>
    set((s) => ({
      placedIds: s.placedIds.filter((x) => x !== id),
      lastPlacedId: s.lastPlacedId === id ? null : s.lastPlacedId,
    })),
  reconcile: (datasetAntennaIds) => {
    const s = get();
    const present = new Set(datasetAntennaIds ?? []);
    const placedIds = s.placedIds.filter((id) => present.has(id));
    const lastPlacedId = s.lastPlacedId !== null && present.has(s.lastPlacedId) ? s.lastPlacedId : null;
    const endPlacement = datasetAntennaIds === null && (s.active || s.target !== null);
    if (placedIds.length === s.placedIds.length && lastPlacedId === s.lastPlacedId && !endPlacement) return;
    set(
      endPlacement ? { placedIds, lastPlacedId, active: false, target: null } : { placedIds, lastPlacedId },
    );
  },
  reset: () =>
    set({
      active: false,
      pending: defaultSpec(),
      target: null,
      placedIds: [],
      issuedIds: [],
      lastPlacedId: null,
    }),
}));
